import { useCallback, useState } from 'react';

const STORAGE_PREFIX = 'col-prefs:';

export interface ColumnDef {
  key: string;
  label: string;
  /** Column cannot be hidden — always rendered. */
  locked?: boolean;
  /** Shown by default when no stored preference exists. */
  defaultVisible?: boolean;
}

export function useColumnPreferences(tableId: string, columns: readonly ColumnDef[]) {
  const storageKey = `${STORAGE_PREFIX}${tableId}`;

  const [visible, setVisible] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as string[];
        const locked = columns.filter((c) => c.locked).map((c) => c.key);
        return new Set([...locked, ...parsed]);
      }
    } catch {}
    return new Set(
      columns.filter((c) => c.locked || c.defaultVisible !== false).map((c) => c.key),
    );
  });

  const toggle = useCallback(
    (key: string) => {
      setVisible((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        const toStore = [...next].filter(
          (k) => !columns.find((c) => c.key === k)?.locked,
        );
        localStorage.setItem(storageKey, JSON.stringify(toStore));
        return next;
      });
    },
    [columns, storageKey],
  );

  const isVisible = useCallback((key: string) => visible.has(key), [visible]);

  return { visible, toggle, isVisible };
}
